import { LeftOutlined, RightOutlined, ShopOutlined } from "@ant-design/icons";
import {
  Alert,
  Button,
  Card,
  ConfigProvider,
  Select,
  Skeleton,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from "antd";
import React, {
  Suspense,
  lazy,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useAuth } from "../../context/AuthContext";
import useApi from "../../utils/useApi";
import useAuthApi from "../../utils/useAuthApi";

const UserManagersModal = lazy(() => import("./UserManagersModal"));
const EditStoreModal = lazy(
  () => import("./stores-management/EditBranchModal")
);
const StoresManagerModal = lazy(
  () => import("./stores-management/StoresManagerModal")
);

const WRAPPER = { width: "100%", display: "flex", justifyContent: "center" };
const INNER = { width: "90%" };
const CENTER = {
  width: "100%",
  alignItems: "center",
  justifyContent: "center",
};

const cap = (s) => (s ? s[0].toUpperCase() + s.slice(1).toLowerCase() : "");

export default function ControlPanel() {
  const {
    getUsersData,
    postUserData,
    patchUserData,
    deleteUserData,
    getUsersWithBranches,
  } = useAuthApi();

  const { user, refreshUser } = useAuth();
  const { getClients, getBranches } = useApi();

  const [showUserModal, setShowUserModal] = useState(false);
  const [showStoresModal, setShowStoresModal] = useState(false);
  const [showEditStoreModal, setShowEditStoreModal] = useState(false);
  const [storesModalStep, setStoresModalStep] = useState("choose");

  const [selectedBranch, setSelectedBranch] = useState(null);
  const [selectedUserId, setSelectedUserId] = useState(null);

  const [users, setUsers] = useState([]);
  const [loggedUser, setLoggedUser] = useState(null);
  const [clients, setClients] = useState([]);
  const [branches, setBranches] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const canManageClientsAndBranches =
    loggedUser?.is_superuser === true ||
    loggedUser?.is_superuser === "true" ||
    loggedUser?.is_client_admin === true;

  const cardsPerPage = 5;
  const [page, setPage] = useState(0);
  const [selectedClientFilter, setSelectedClientFilter] = useState(null);

  useEffect(() => {
    (async () => {
      if (!user?.user_id) {
        setIsLoading(false);
        return;
      }
      setIsLoading(true);
      try {
        const [single, usersBranches, fetchedClients, fetchedBranches] =
          await Promise.all([
            getUsersData(user.user_id),
            getUsersWithBranches(),
            getClients(true),
            getBranches(undefined, true),
          ]);

        setLoggedUser(Array.isArray(single) ? single[0] : single);
        setUsers(Array.isArray(usersBranches) ? usersBranches : []);
        setClients(fetchedClients?.data || fetchedClients);
        setBranches(fetchedBranches?.data || fetchedBranches);
      } finally {
        setIsLoading(false);
      }
    })();
  }, [user?.user_id]);

  const clientMap = useMemo(() => {
    const m = {};
    clients.forEach((c) => (m[c.id] = c.name));
    return m;
  }, [clients]);

  // Add logic to detect clients without branches
  const clientsWithoutBranches = useMemo(() => {
    const clientIdsWithBranches = new Set(branches.map((b) => b.client));
    return clients.filter(
      (c) => c.is_active && !clientIdsWithBranches.has(c.id)
    );
  }, [clients, branches]);

  const branchCards = useMemo(() => {
    const all = branches.map((b) => ({
      id: b.id,
      branchName: b.name,
      clientName: clientMap[b.client] || "—",
      clientId: b.client,
      ip: b.ip_address,
      store_code: b.store_code,
      total_area_m2: b.total_area_m2,
      is_active: b.is_active,
    }));
    if (selectedClientFilter) {
      return all.filter((c) => c.clientId === selectedClientFilter);
    }
    return all;
  }, [branches, clientMap, selectedClientFilter]);

  const totalPages = Math.max(1, Math.ceil(branchCards.length / cardsPerPage));

  useEffect(() => {
    if (page >= totalPages) setPage(totalPages - 1);
  }, [totalPages, page]);

  useEffect(() => {
    setPage(0);
  }, [selectedClientFilter]);

  const currentCards = useMemo(
    () =>
      branchCards.slice(
        page * cardsPerPage,
        page * cardsPerPage + cardsPerPage
      ),
    [branchCards, page]
  );

  const columns = useMemo(
    () => [
      { title: "Usuário", dataIndex: "usuario", key: "usuario" },
      { title: "Email", dataIndex: "email", key: "email" },
      { title: "Cargo", dataIndex: "cargo", key: "cargo" },
      { title: "Cliente", dataIndex: "cliente", key: "cliente" },
      { title: "Filial", dataIndex: "filiais", key: "filiais" },
    ],
    []
  );

  const dataSource = useMemo(
    () =>
      users.map((u) => {
        const branchList = u.branches || [];
        const branchNames = branchList.map((b) => b.branch_name);
        const clientNames = [...new Set(branchList.map((b) => b.client_name))];
        const isSuperAdmin = u.role === "SuperAdmin";
        const isAdministrador = u.role === "Administrador";

        return {
          key: u.id,
          usuario: `${u.first_name} ${u.last_name}`,
          email: u.email,
          cargo: u.role,
          cliente: isSuperAdmin
            ? "Todos os Clientes"
            : isAdministrador
              ? u.client_name || "—"
              : clientNames.length
                ? clientNames.join(", ")
                : "—",
          filiais:
            isSuperAdmin || isAdministrador
              ? "Todas as Filiais"
              : branchNames.length
                ? branchNames.join(", ")
                : "—",
        };
      }),
    [users]
  );

  const handleUserRowClick = useCallback((record) => {
    setSelectedUserId(record.key);
    setShowUserModal(true);
  }, []);

  const handleOpenBranchModal = useCallback(() => {
    setStoresModalStep("branch");
    setShowStoresModal(true);
  }, []);

  const handleRefreshData = useCallback(
    async (successMessage = null) => {
      setIsLoading(true);
      try {
        const [fetchedClients, fetchedBranches] = await Promise.all([
          getClients(true),
          getBranches(undefined, true),
        ]);
        setClients(fetchedClients?.data || fetchedClients);
        setBranches(fetchedBranches?.data || fetchedBranches);

        // Refresh user data to update available_branches in BranchSelector
        await refreshUser();

        if (successMessage) {
          message.success(successMessage);
        }
      } finally {
        setIsLoading(false);
      }
    },
    [getClients, getBranches, refreshUser]
  );

  if (isLoading) {
    return (
      <ConfigProvider>
        <div style={WRAPPER}>
          <div style={{ ...INNER, marginTop: 20 }}>
            <Skeleton active />
          </div>
        </div>
      </ConfigProvider>
    );
  }

  return (
    <ConfigProvider
      theme={{
        components: { Card: { headerBg: "var(--ouran-card-header-bg)" } },
      }}>
      <div style={WRAPPER}>
        <div style={INNER}>
          <Card title="Painel de Controle">
            <Typography.Title level={4} style={{ marginTop: 8 }}>
              {loggedUser
                ? `Seja bem-vindo, ${cap(loggedUser.first_name)} ${cap(
                    loggedUser.last_name
                  )}`
                : "Seja bem-vindo"}
            </Typography.Title>
            <Typography.Text strong>
              Cargo: {loggedUser?.role || "N/A"}
            </Typography.Text>

            <Card
              title="Quadro de Clientes e Filiais"
              style={{ marginTop: 24, marginBottom: 20 }}
              bordered={false}>
              {canManageClientsAndBranches &&
                clientsWithoutBranches.length > 0 && (
                  <Alert
                    message="Atenção: Clientes sem filiais"
                    description={
                      <div>
                        <p>
                          {clientsWithoutBranches.length === 1
                            ? "Existe 1 cliente sem filiais cadastradas:"
                            : `Existem ${clientsWithoutBranches.length} clientes sem filiais cadastradas:`}
                        </p>
                        <ul style={{ margin: "8px 0", paddingLeft: "20px" }}>
                          {clientsWithoutBranches.map((client) => (
                            <li key={client.id}>
                              <strong>{client.name}</strong>
                            </li>
                          ))}
                        </ul>
                        <p>
                          Clique em - Gerenciar Clientes e Filiais - para
                          cadastrar filiais para estes clientes.
                        </p>
                      </div>
                    }
                    type="warning"
                    showIcon
                    style={{ marginBottom: 16 }}
                  />
                )}

              {canManageClientsAndBranches && (
                <Button
                  type="primary"
                  style={{ marginBottom: 16 }}
                  onClick={() => {
                    setStoresModalStep("choose");
                    setShowStoresModal(true);
                  }}>
                  Gerenciar Clientes e Filiais
                </Button>
              )}

              <div style={{ marginBottom: 16 }}>
                <Typography.Title level={5} style={{ margin: "8px 0 4px" }}>
                  {loggedUser.is_superuser
                    ? "Filiais cadastradas na aplicação"
                    : "Você está cadastrado nas seguintes lojas"}
                </Typography.Title>
                <Typography.Text type="secondary">
                  Clique no item que deseja editar/excluir
                </Typography.Text>
              </div>

              <div style={{ marginBottom: 16, maxWidth: 320 }}>
                <Typography.Text
                  strong
                  style={{ display: "block", marginBottom: 4 }}>
                  Filtrar por cliente
                </Typography.Text>
                <Select
                  allowClear
                  placeholder="Todos os clientes"
                  style={{ width: "100%" }}
                  value={selectedClientFilter}
                  onChange={(v) => setSelectedClientFilter(v || null)}>
                  {clients.map((c) => (
                    <Select.Option key={c.id} value={c.id}>
                      {c.name}
                      {!c.is_active && (
                        <Tag
                          color="default"
                          style={{ marginLeft: 8, fontSize: 11 }}>
                          inativo
                        </Tag>
                      )}
                    </Select.Option>
                  ))}
                </Select>
              </div>

              <Space style={CENTER}>
                <Button
                  shape="circle"
                  icon={<LeftOutlined />}
                  disabled={page === 0}
                  onClick={() => setPage((p) => Math.max(p - 1, 0))}
                />
                {currentCards.map((card) => (
                  <Card
                    key={card.id}
                    hoverable
                    style={{
                      width: 170,
                      height: 160,
                      textAlign: "center",
                      marginRight: 16,
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                      alignItems: "center",
                      opacity: card.is_active ? 1 : 0.5,
                    }}
                    onClick={() => {
                      setSelectedBranch(card);
                      setShowEditStoreModal(true);
                    }}>
                    <ShopOutlined
                      style={{
                        fontSize: 28,
                        marginBottom: 8,
                        color: card.is_active ? undefined : "#bfbfbf",
                      }}
                    />
                    <div>
                      <Typography.Text strong>
                        {card.clientName}
                      </Typography.Text>
                    </div>
                    <div>
                      <Typography.Text>{card.branchName}</Typography.Text>
                    </div>
                    {!card.is_active && (
                      <Tag
                        color="default"
                        style={{ marginTop: 6, fontSize: 11 }}>
                        inativa
                      </Tag>
                    )}
                  </Card>
                ))}
                <Button
                  shape="circle"
                  icon={<RightOutlined />}
                  disabled={page === totalPages - 1}
                  onClick={() =>
                    setPage((p) => Math.min(p + 1, totalPages - 1))
                  }
                />
              </Space>
            </Card>

            <Card
              title="Quadro de Usuários"
              style={{ marginBottom: 20, marginTop: 35 }}>
              <Button
                type="primary"
                style={{ marginBottom: 24 }}
                onClick={() => {
                  setSelectedUserId(null);
                  setShowUserModal(true);
                }}>
                Gerenciar Usuários
              </Button>

              <Table
                columns={columns}
                dataSource={dataSource}
                pagination={{
                  pageSize: 7,
                  showSizeChanger: false,
                }}
                onRow={(record) => ({
                  onClick: () => handleUserRowClick(record),
                  style: { cursor: "pointer" },
                })}
              />
            </Card>
          </Card>
        </div>
      </div>

      <Suspense fallback={null}>
        {showUserModal && (
          <UserManagersModal
            open={showUserModal}
            onClose={() => setShowUserModal(false)}
            users={users}
            setUsers={setUsers}
            postUserData={postUserData}
            patchUserData={patchUserData}
            deleteUserData={deleteUserData}
            editUserId={selectedUserId}
          />
        )}

        {showStoresModal && (
          <StoresManagerModal
            open={showStoresModal}
            onClose={() => setShowStoresModal(false)}
            initialStep={storesModalStep}
            onOpenBranchModal={handleOpenBranchModal}
            onRefresh={handleRefreshData}
          />
        )}

        {showEditStoreModal && (
          <EditStoreModal
            open={showEditStoreModal}
            onClose={() => setShowEditStoreModal(false)}
            branch={selectedBranch}
            clients={clients}
            refresh={async () => {
              setShowEditStoreModal(false);
              setIsLoading(true);
              const bs = await getBranches(undefined, true);
              setBranches(bs?.data || bs);
              // Refresh user data to update available_branches in BranchSelector
              await refreshUser();
              setIsLoading(false);
            }}
          />
        )}
      </Suspense>
    </ConfigProvider>
  );
}
